import type { SupabaseClient } from '@supabase/supabase-js';

export const LISTING_IMAGE_MAX_BYTES = 15 * 1024 * 1024;
export const LISTING_IMAGE_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
] as const;

export function validateListingImageFile(file: File): void {
  const mime = file.type.trim().toLowerCase();
  if (!(LISTING_IMAGE_ALLOWED_MIME_TYPES as readonly string[]).includes(mime)) {
    throw new Error(
      `File "${file.name}" uses an unsupported format. Choose a JPEG or PNG image.`
    );
  }
  if (file.size > LISTING_IMAGE_MAX_BYTES) {
    throw new Error(`File "${file.name}" is too large. Maximum size is 15MB.`);
  }
}

/**
 * Sanitize filename:
 * - Lowercase
 * - Replace spaces with '-'
 * - Strip non [a-z0-9.-]
 * - Collapse dashes
 */
export function sanitizeFilename(name: string): string {
  // Extract extension
  const lastDot = name.lastIndexOf('.');
  const ext = lastDot >= 0 ? name.slice(lastDot).toLowerCase() : '';
  const base = lastDot >= 0 ? name.slice(0, lastDot) : name;

  // Sanitize: lowercase, replace spaces with '-', strip non [a-z0-9.-], collapse dashes
  const sanitized = base
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9.-]/g, '') // Strip non [a-z0-9.-]
    .replace(/-+/g, '-') // Collapse multiple dashes
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes

  // Ensure we have something
  const finalBase = sanitized || 'image';

  return `${finalBase}${ext}`;
}

/**
 * Build storage path for listing image:
 * listings/<listing_id>/<uuid>-<sanitized_filename>
 */
export function buildListingImagePath(listingId: string, filename: string): string {
  const uuid = crypto.randomUUID();
  const sanitized = sanitizeFilename(filename);
  return `listings/${listingId}/${uuid}-${sanitized}`;
}

export type UploadListingImagesProgress = {
  current: number;
  total: number;
  fileName: string;
};

/**
 * Upload listing images to Supabase Storage
 *
 * @param supabase - Supabase client
 * @param listingId - Listing ID
 * @param files - Files to upload
 * @param onProgress - Optional progress callback (1-based current index)
 * @returns Array of storage keys (paths), NOT public URLs
 * @throws Error if upload fails
 */
export async function uploadListingImages(
  supabase: SupabaseClient,
  listingId: string,
  files: File[],
  onProgress?: (progress: UploadListingImagesProgress) => void
): Promise<string[]> {
  if (files.length === 0) {
    return [];
  }

  const storageKeys: string[] = [];
  const BUCKET_NAME = 'listing-images'; // Exact bucket name

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress?.({ current: i + 1, total: files.length, fileName: file.name });

    // This is only an early client check. The bucket repeats MIME/size
    // enforcement, and Stage B must still validate the actual bytes.
    validateListingImageFile(file);

    // Build storage path
    const path = buildListingImagePath(listingId, file.name);

    // Upload to storage with upsert=false (fail if file exists)
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      throw new Error(`Failed to upload "${file.name}": ${uploadError.message}`);
    }

    // Return the storage key (path), not the public URL
    storageKeys.push(path);
  }

  // Strict invariant: returned keys must equal input files
  if (storageKeys.length !== files.length) {
    throw new Error(
      `Upload invariant violation: expected ${files.length} storage keys, got ${storageKeys.length}`
    );
  }

  // Dev-only logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[uploadListingImages]', {
      listingId,
      filesCount: files.length,
      returnedKeys: storageKeys.length,
      firstKey: storageKeys[0] || null,
      bucket: BUCKET_NAME,
      allKeys: storageKeys,
    });
  }

  return storageKeys;
}

/**
 * Delete listing image from Supabase Storage
 *
 * @param supabase - Supabase client
 * @param imageValue - Either a full public URL (starts with "http") or a storage key (path)
 * @throws Error if deletion fails (best-effort: caller should handle gracefully)
 */
export async function deleteListingImage(
  supabase: SupabaseClient,
  imageValue: string
): Promise<void> {
  let storageKey: string;

  if (imageValue.startsWith('http')) {
    // Parse pathname after /storage/v1/object/public/listing-images/
    const publicUrlPattern = /\/storage\/v1\/object\/public\/listing-images\/(.+)$/;
    const match = imageValue.match(publicUrlPattern);

    if (match && match[1]) {
      storageKey = match[1];
    } else {
      throw new Error(`Could not parse storage key from URL: ${imageValue}`);
    }
  } else {
    // Treat as storage key directly
    storageKey = imageValue;
  }

  // Remove from storage
  const { error } = await supabase.storage
    .from('listing-images')
    .remove([storageKey]);

  if (error) {
    throw new Error(`Failed to delete image from storage: ${error.message}`);
  }
}

/**
 * Check if a string is a blob URL
 */
export function isBlobUrl(s: string): boolean {
  return s.startsWith('blob:');
}
